import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../utils/buildingSelection';
import { buildingUtilityApi } from '../../api/buildingUtilityApi';
import { buildingsApi, Building } from '../../api/buildingsApi';
import { BuildingUtility } from '../../types';
import { useCrudPermissions } from '../../hooks/useCrudPermissions';
import { BUILDING_UTILITY_PERMISSIONS } from '../../constants/functionPermissions';

// Helper function to get display capacity from serviceHours
const getDisplayCapacity = (utility: BuildingUtility): string => {
  if (!utility.serviceHours || utility.serviceHours.length === 0) {
    return '-';
  }

  // Check if any service hour has unlimited capacity (maxCapacity = 0 or missing)
  const hasUnlimited = utility.serviceHours.some(hour => !hour.maxCapacity || hour.maxCapacity === 0);
  
  if (hasUnlimited) {
    return 'Không giới hạn';
  }

  // Sum up all service hours' capacity
  const totalCapacity = utility.serviceHours.reduce((sum, hour) => sum + (hour.maxCapacity || 0), 0);
  
  if (totalCapacity === 0) {
    return 'Không giới hạn';
  }

  // Display format based on utility type
  if (utility.group?.utilityType === 'NO_BOOKING') {
    return `${totalCapacity} người`;
  } else {
    return `${totalCapacity} slot`;
  }
};

export const BuildingUtilityList: React.FC = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const { canCreate, canEdit, canDelete } = useCrudPermissions(BUILDING_UTILITY_PERMISSIONS);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState<string>('');
  const [utilities, setUtilities] = useState<BuildingUtility[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBuildings();
  }, []);

  useEffect(() => {
    if (selectedBuildingId) {
      loadUtilities();
    } else {
      setUtilities([]);
    }
  }, [selectedBuildingId]);

  const loadBuildings = async () => {
    try {
      const response = await buildingsApi.getBuildings();
      if (response && Array.isArray(response)) {
        setBuildings(response);
        setSelectedBuildingId((prev) => applyDefaultBuildingSelection(response, user, prev));
      }
    } catch (err: any) {
      setError('Error loading buildings');
    }
  };

  const loadUtilities = async () => {
    if (!selectedBuildingId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await buildingUtilityApi.getUtilities(selectedBuildingId);
      
      if (response && Array.isArray(response.data)) {
        setUtilities(response.data);
      } else {
        console.warn('Expected data with array but got:', response);
        setUtilities([]);
        setError('Invalid data format received from server');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error loading utilities');
      setUtilities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Xóa tiện ích "${name}"?`)) {
      return;
    }

    try {
      await buildingUtilityApi.deleteUtility(id);
      await loadUtilities();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error deleting utility');
    }
  };

  return (
    <div className="max-w-6xl mx-auto py-8">
      {/* Header */}
      <div className="mb-8">
        <nav className="text-sm text-gray-500 mb-4">
          <span>Tiện ích tòa nhà</span> / <span className="text-gray-900">Danh sách tiện ích</span>
        </nav>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Danh sách tiện ích</h1>
        <p className="text-gray-600">Quản lý các tiện ích trong tòa nhà</p>
      </div>

      {/* Building Selection */}
      {shouldShowBuildingSelector(buildings) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Chọn tòa nhà:</label>
            <select
              value={selectedBuildingId}
              onChange={(e) => selectBuilding(user?.id, e.target.value, setSelectedBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">-- Chọn tòa nhà --</option>
              {buildings.map((building) => (
                <option key={building.id} value={building.id}>
                  {building.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {selectedBuildingId && (
        <>
          {/* Actions Bar */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-wrap gap-4 items-center justify-between">
              <div className="flex gap-4">
                {canCreate && (
                  <Link
                    to={`/building-utilities/add?buildingId=${selectedBuildingId}`}
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 shadow-md"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Tạo tiện ích mới
                  </Link>
                )}
              </div>

              <div className="text-sm text-gray-500">
                Tổng: <span className="font-semibold text-gray-900">{utilities.length}</span> tiện ích
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-red-800">{error}</span>
                <button
                  onClick={loadUtilities}
                  className="ml-auto px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                >
                  Thử lại
                </button>
              </div>
            </div>
          )}

          {/* Utilities Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="text-lg text-gray-600">Đang tải danh sách tiện ích...</div>
              </div>
            ) : utilities.length === 0 ? (
              <div className="p-8 text-center">
                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Chưa có tiện ích nào</h3>
                <p className="text-gray-500 mb-4">Bắt đầu bằng cách tạo tiện ích mới</p>
                {canCreate && (
                  <Link
                    to={`/building-utilities/add?buildingId=${selectedBuildingId}`}
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200"
                  >
                    Tạo tiện ích mới
                  </Link>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        STT
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tên tiện ích
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nhóm tiện ích
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vị trí
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sức chứa
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Trạng thái
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ngày tạo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Thao tác
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {utilities.map((utility, index) => (
                      <tr key={utility.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {index + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white font-medium text-sm">
                              {utility.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{utility.name}</div>
                              {utility.description && (
                                <div className="text-sm text-gray-500">{utility.description}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="text-lg mr-2">{utility.group?.icon || '🏢'}</span>
                            <span className="text-sm text-gray-900">{utility.group?.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {utility.location || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {getDisplayCapacity(utility)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {utility.isActive ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Hoạt động
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Không hoạt động
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(utility.createdAt).toLocaleDateString('vi-VN')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            {canEdit && (
                              <Link
                                to={`/building-utilities/${utility.id}/edit`}
                                className="text-indigo-600 hover:text-indigo-900 transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </Link>
                            )}

                            {canDelete && (
                              <button
                                onClick={() => handleDelete(utility.id, utility.name)}
                                className="text-red-600 hover:text-red-900 transition-colors"
                                title="Xóa"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}; 